import { useLayoutEffect, useRef } from "react";

/**
 * A ref for a list whose items rise into place, one after the other, the
 * first time the list scrolls into view (styles/motion.css, `.tk-stagger`;
 * each item says its own `--tk-delay`).
 *
 * The items are in the page and readable from the first render: only their
 * opacity and transform move. They are hidden for the animation only when
 * the list starts below the screen and the browser can say when it arrives
 * (IntersectionObserver); a list already on screen rises at once, and a
 * reduced-motion visitor, a browser without IntersectionObserver or a
 * printout gets every item still and visible.
 */
export default function useRevealOnScroll() {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined" || typeof window.IntersectionObserver !== "function") return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const box = el.getBoundingClientRect();
    const height = window.innerHeight || document.documentElement.clientHeight || 0;
    if (box.top < height && box.bottom > 0) {
      el.dataset.reveal = "in";
      return undefined;
    }
    el.dataset.reveal = "pending";
    const observer = new window.IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      el.dataset.reveal = "in";
      observer.disconnect();
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.05 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

/** The delay of the `index`-th item: 60ms apart, the ninth and later together. */
export const staggerDelay = (index) => `${Math.min(Math.max(index, 0), 8) * 60}ms`;
